import { confirmFacePhotoUploadResultSchema, type FaceKey } from '@repo/api-contracts';
import {
  type CompositeAssetKind,
  compositeSlotKey,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { COMPOSITE_ASSET_KINDS } from './composite-asset-kinds';
import { type FaceAssetUpload, useFaceAssetUpload } from './use-face-asset-upload';

/**
 * 면·종류 칸당 전송 하나. 사진 업로드와 같은 presign → 임시 PUT → confirm 흐름을 그 종류의 용도로 돌리고,
 * 확정 키가 이 상품·이 종류 세그먼트일 때만 합성 설정에 반영한다. 알파 없음 같은 BAD_REQUEST 는 재시도 대신
 * 다른 파일을 안내한다. 표면·마스크 삭제(D-E)·면 끄기·저장 버전 적용은 작업 칸을 지워 진행 중 전송을 무효로 한다.
 */
export const useCompositeAssetUpload = (
  face: FaceKey,
  kind: CompositeAssetKind,
): FaceAssetUpload => {
  const store = useEditAreaDraftStore();
  const spec = COMPOSITE_ASSET_KINDS[kind];
  return useFaceAssetUpload({
    store,
    slot: compositeSlotKey(face, kind),
    purpose: spec.purpose,
    contentTypeMessage: spec.contentTypeMessage,
    label: spec.label,
    isSlotAlive: (state) =>
      state.draft.activeFaces.includes(face) && state.faces[face] !== undefined,
    begin: (operationId) => store.getState().beginCompositeOperation(face, kind, operationId),
    setStatus: (operationId, status) =>
      store.getState().setCompositeOperationStatus(face, kind, operationId, status),
    cancel: (operationId) => store.getState().cancelCompositeOperation(face, kind, operationId),
    parseConfirmed: (result, productId) => {
      const parsed = confirmFacePhotoUploadResultSchema.safeParse(result);
      if (
        !parsed.success ||
        !parsed.data.photoKey.startsWith(`products/${productId.toLowerCase()}/${spec.segment}/`)
      )
        return null;
      return { key: parsed.data.photoKey, size: parsed.data.photoSize, url: parsed.data.photoUrl };
    },
    reflect: (operationId, asset) =>
      store.getState().setCompositeAsset(face, kind, asset, operationId),
    fileRejectedCodes: ['BAD_REQUEST'],
  });
};
